/**
 * @file   src\components\Onboarding\Dashboard\LeftpanelTabContent.js
 * @brief  This file is responsible for general tab functionality
 * @date   April, 2023
 * @author ZCO Engineer
 * @copyright (c) 2023, ZCO
 */
import React, { useEffect, useState, useContext, useRef } from "react";
import { useDebounce } from "use-debounce";
import FilterCategory from "./FilterCategory";
import FilterLocationSearch from "../FilterLocationSearch";
import FilterKeywordSearch from "../FilterKeywordSearch";
import Accordion from "react-bootstrap/Accordion";
import { filterContext } from "../../../../pages/v2/Home";
import {
  accsPvtBthRmchkBxFilter,
  adaAccessibleChkBx,
  alwPetsChkBx,
  BUY,
  DEFAULT_MAX_BUDGET_BUY,
  DEFAULT_MAX_BUDGET_LTR,
  DEFAULT_MAX_BUDGET_STR,
  DEFAULT_STEP_VALUE_BUY,
  DEFAULT_STEP_VALUE_LTR,
  DEFAULT_STEP_VALUE_STR,
  INDEX_TO_BE_SLICE_15,
  LONG_RENT,
  pvtEntrnceChkBx,
} from "../../../../constants/common";
import { getPropertyTypes } from "../../../../services/commonServices";
import { getSquareFeetList } from "../../../../services/wantAdService";
import { handleRadioButonChange } from "../../../../actions/common";
// JAKE-FILTER-IMPROVEMENTS: Import histogram slider (replaces RangeSliderComponent)
import PriceHistogramSlider from "../../../Common/PriceHistogramSlider";
import SelectDropDown from "../../../Common/SelectDropDown";
import {
  convertToNumber,
  handleNegativeInput,
} from "../../../../helpers/validations";
import TextBox from "../../../Common/TextBox";
import {
  replaceComma,
  getLocalStorage,
  replaceString,
  thousandSeperatedValue,
  thousandSeperatedValueFilter,
  getPriceText,
  checkIsNumber,
  arraySplice,
  filterArrayBasedOnKey,
} from "../../../../helpers/common";
import DateRangeSelector from "../../../DateRangeSelector";
import { usePrevious } from "../../../../hooks/usePrevious";
import PremiumPlanModal from "../../../../components/PlanUpgrade/PremiumPlanModal";
import { useUserDetails } from "@/hooks";
import { formatThousandSeparatedValue } from "@/helpers";
import { FilterBarSkeleton } from "../../../Common/Skeletons";


const LeftpanelTabContent = (props) => {
  const {
    updateFilter,
    filterQuery,
    setUserLocation,
    gitchaNotAvailble,
    checkGitchaAvailableInLoc,
    userLocation,
    mlsRestrictedCities,
  } = props;

  const {
    userDetails: userDetailsFromHook,
    loadUserData,
  } = useUserDetails();

  const [location, setLocation] = useState(() => {
    if (userLocation?.city && userLocation?.state) {
      return `${userLocation.city}, ${userLocation.state}`;
    }
    if (userLocation?.formatted_address) {
      return userLocation.formatted_address;
    }
    return "";
  });
  const [propertyList, setPropertyList] = useState([]);
  const [typeOfPlaceList, setTypeOfPlaceList] = useState([]);
  const [selectedTypeOfPlace, setSelectedTypeOfPlace] = useState([]);
  const [budgetRange, setBudgetRange] = useState("");
  const [minError, setMinError] = useState("");
  const [maxError, setMaxError] = useState("");
  // [US-003] Dynamic cap state - replaces static DEFAULT_MAX_BUDGET for slider
  const [dynamicMax, setDynamicMax] = useState(null);
  const [dynamicStep, setDynamicStep] = useState(null);
  const [isCapFallback, setIsCapFallback] = useState(true);
  const capAdjustingRef = useRef(false);

  const getSelectedItems = (property) => {
    const selectedItemArray = _.get(filterQuery, property, []);
    return selectedItemArray;
  };

  const [searchKeyword, setSearchKeyword] = useState("");
  const [squareFootageMinValue, setSquareFootageMinValue] = useState(null);
  const [squareFootageMaxValue, setSquareFootageMaxValue] = useState(null);
  const filterCntxt = useContext(filterContext);
  const [propertyCount, setPropertyCount] = useState(0);
  const [typeOfPlaceCount, setTPCount] = useState(0);
  // JAKE-FILTER-IMPROVEMENTS: Extract adList for price histogram
  const { adType, clearFilterQuery, adList } = filterCntxt;
  const [isPrivateRoomSelected, setIsPrivateRoomSelected] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);
  const [squareFeetMinList, setSqareFeetMinList] = useState([]);
  const [squareFeetMaxList, setSqareFeetMaxList] = useState([]);
  const [isAgentOnly, setIsAgentOnly] = useState(false);
  // Skeleton loading - show skeleton until initial filter data resolves
  const [filterBarReady, setFilterBarReady] = useState(false);

  const [showPremiumPlanModal, setPremiumPlanModal] = useState(false);
  const PremiumPlanModalClose = () => setPremiumPlanModal(false);
  const PremiumPlanModalShow = () => setPremiumPlanModal(true);

  const MIN_SF_DROPDOWN = "minSFDrop";
  const MAX_SF_DROPDOWN = "maxSFDrop";
  const minRef = useRef(null);
  const maxRef = useRef(null);
  let DEFAULT_MAX_BUDGET;
  const DEFAULT_MIN_BUDGET = 0;
  let DEFAULT_STEP_VALUE;

  if (adType === BUY) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_BUY;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_BUY;
  } else if (adType === LONG_RENT) {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_LTR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_LTR;
  } else {
    DEFAULT_MAX_BUDGET = DEFAULT_MAX_BUDGET_STR;
    DEFAULT_STEP_VALUE = DEFAULT_STEP_VALUE_STR;
  }

  const budgetMin = _.get(filterQuery, "priceMin");
  const budgetMax = _.get(filterQuery, "priceMax");
  const STR_OPTION_DETAILS_PVT_ROOM = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
    { id: accsPvtBthRmchkBxFilter, text: "Access to private bathroom" },
    { id: pvtEntrnceChkBx, text: "Private Entrance" },
  ];

  const STR_OPTION_DETAILS_ALL = [
    { id: adaAccessibleChkBx, text: "ADA Accessible" },
    { id: alwPetsChkBx, text: "Allows pets" },
  ];

  const formatSqFtLabel = (val, originalLabel = "") => {
    if (val === null || val === undefined) return originalLabel;
    const hasPlus = String(originalLabel).includes("+");
    return formatThousandSeparatedValue(Number(val)) + (hasPlus ? "+" : "");
  };

  // Sync location display when userLocation prop changes (cache restore, GeoLocation, etc.)
  useEffect(() => {
    if (userLocation?.city && userLocation?.state) {
      setLocation(`${userLocation.city}, ${userLocation.state}`);
    } else if (userLocation?.formatted_address) {
      setLocation(userLocation.formatted_address);
    }
  }, [userLocation]);

  useEffect(() => {
    setIsAgentOnly(false);
  }, [adType]);

  useEffect(() => {
    setFilterBarReady(false);

    const propertyPromise = adType === BUY
      ? getPropertyTypes(adType)
          .then((response) => {
            if (response.errorCode === 0 && _.get(response, "result", null)) {
              setPropertyList(_.get(response, "result", null));
            }
          })
          .catch((err) => { console.log("Error", err); })
      : Promise.resolve(setPropertyList([]));

    // [US-003] Reset dynamic cap on adType change - will be recalculated by histogram
    setDynamicMax(null);
    setDynamicStep(null);
    setIsCapFallback(true);
    let DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, DEFAULT_MAX_BUDGET];
    if (budgetMin && !budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, DEFAULT_MAX_BUDGET];
    } else if (!budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [DEFAULT_MIN_BUDGET, budgetMax];
    } else if (budgetMin && budgetMax) {
      DEFAULT_BUDGET_RANGE = [budgetMin, budgetMax];
    }
    setBudgetRange(DEFAULT_BUDGET_RANGE);
    setSelectedTypeOfPlace([10, 11, 6]);

    const sqftPromise = getSquareFeetList().then(({ errorCode, result }) => {
      if (errorCode !== 0 || !result) return;

      const baseList = result.map((itm) => ({
        value: itm.Sqft_Value,
        label: formatSqFtLabel(itm.Sqft_Value, itm.Sqft_Value_Text),
      }));

      setSqareFeetMinList([{ value: null, label: "No min" }, ...baseList]);
      setSqareFeetMaxList([{ value: null, label: "No max" }, ...baseList]);

      setSquareFootageMaxValue({ value: null, label: "No max" });
      setSquareFootageMinValue({ value: null, label: "No min" });

      setStartDate(null);
      setEndDate(null);
    });

    Promise.all([propertyPromise, sqftPromise.catch(() => {})]).then(() => setFilterBarReady(true));
  }, [adType]);

  useEffect(() => {
    if (adType !== BUY) {
      let type = "";
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10)
      ) {
        type = "es-pr";
      } else if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "pr-land";
      } else if (
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "es-land";
      }
      if (
        selectedTypeOfPlace.includes(11) &&
        selectedTypeOfPlace.includes(10) &&
        selectedTypeOfPlace.includes(6)
      ) {
        type = "all";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(11)) {
        type = "privateroom";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(10)) {
        type = "entirespace";
      }
      if (selectedTypeOfPlace.length == 1 && selectedTypeOfPlace.includes(6)) {
        type = "land";
      } else {
        setPropertyList([]);
      }
      getPropertyTypes(type)
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setPropertyList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });

      if (selectedTypeOfPlace.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }
    }
  }, [selectedTypeOfPlace]);

  // Debounce budget values so the API only fires after the user pauses dragging
  const [debouncedBudgetMin] = useDebounce(budgetRange[0], 400);
  const [debouncedBudgetMax] = useDebounce(budgetRange[1], 400);
  // Track previous debounced values (not raw) to detect real changes after debounce settles
  const prevMinBdgt = usePrevious(debouncedBudgetMin);
  const prevMaxBdgt = usePrevious(debouncedBudgetMax);

  useEffect(() => {
    updateRangeSliders();
  }, [startDate, endDate]);

  useEffect(() => {
    if (prevMinBdgt && prevMinBdgt !== debouncedBudgetMin) {
      updateFilter({ priceMin: debouncedBudgetMin });
    }
  }, [debouncedBudgetMin]);

  useEffect(() => {
    // Skip API call when budget range changed due to cap initialization
    if (capAdjustingRef.current) {
      capAdjustingRef.current = false;
      return;
    }
    // [US-003] Open-ended when at dynamic max (or static fallback)
    const effectiveMax = dynamicMax || DEFAULT_MAX_BUDGET;
    const mxBdgt = debouncedBudgetMax === effectiveMax ? "" : debouncedBudgetMax;
    if (prevMaxBdgt && prevMaxBdgt !== debouncedBudgetMax) {
      updateFilter({ priceMin: budgetMin, priceMax: mxBdgt });
    }
  }, [debouncedBudgetMax]);

  const updateRangeSliders = async () => {
    updateFilter({ fromDate: startDate, toDate: endDate });
  };

  useEffect(() => {
    if (adType !== BUY) {
      ///      For loading type of place
      getPropertyTypes("rent")
        .then((response) => {
          if (response.errorCode === 0 && _.get(response, "result", null)) {
            const propertyTypes = _.get(response, "result", null);
            setTypeOfPlaceList(propertyTypes);
          }
        })
        .catch((err) => {
          console.log("Error", err);
        });
    }
  }, [adType]);
  /**
   * To handle clear filter
   */
  const clearFilter = () => {
    setUserLocation(null);
    setLocation("");
    setSearchKeyword("");
    clearFilterQuery();
    setSquareFootageMinValue({ value: null, label: "No min" });
    setSquareFootageMaxValue({ value: null, label: "No max" });
    // [US-003] Reset to dynamic max (or static fallback)
    setBudgetRange([DEFAULT_MIN_BUDGET, dynamicMax || DEFAULT_MAX_BUDGET]);
    setPropertyCount(0);
    setTPCount(0);
  };

  /**
   * To handle property selection
   * @param {*} e
   */
  const handlePropertyClick = (e) => {
    const value = convertToNumber(handleRadioButonChange(e));
    const selectedItemArray = getSelectedItems("propertyType");
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      updateFilter({ propertyType: itemArray });
      setPropertyCount(itemArray.length);
    }
  };

  const handleAdditionalRequirementsClick = (e, typeId) => {
    const selectedItemArray = getSelectedItems(e.target.value);
    if (selectedItemArray == 0 || selectedItemArray == null) {
      updateFilter({ [typeId]: 1 });
    } else {
      updateFilter({ [typeId]: 0 });
    }
  };
  /**
   * To handle type of place selection
   * @param {*} e
   */
  const handleTypeOfPlaceClick = (e) => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const value = convertToNumber(handleRadioButonChange(e));
    if (value) {
      let itemArray;
      if (_.includes(selectedItemArray, value)) {
        itemArray = selectedItemArray.filter((e) => e !== value);
      } else {
        itemArray = [...selectedItemArray, value];
      }
      setSelectedTypeOfPlace(itemArray);
      if (itemArray.includes(11)) {
        setIsPrivateRoomSelected(true);
      } else {
        setIsPrivateRoomSelected(false);
      }

      updateFilter({ typeOfPlace: itemArray });
      setTPCount(itemArray.length);
    }
  };
  /**
   * To load property types
   * @returns
   */
  const loadPropertyMain = () => {
    const selectedItemArray = getSelectedItems("propertyType");
    const List =
      propertyList &&
      propertyList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handlePropertyClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };
  /**
   * To load additional requirements section
   * @returns
   */
  const loadAdditionalRequirements = () => {
    const options = isPrivateRoomSelected
      ? STR_OPTION_DETAILS_PVT_ROOM
      : STR_OPTION_DETAILS_ALL;

    const List =
      options &&
      options.map((pt) => {
        const typeId = _.get(pt, "id", "");
        const typeText = _.get(pt, "text", "");
        const isChecked = getSelectedItems(typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              type="checkbox"
              name="chkType"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked == 1 ? true : false}
              onChange={(e) => handleAdditionalRequirementsClick(e, typeId)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  /**
   * To load types of place view
   * @returns
   */
  const loadTypeOfPlace = () => {
    const selectedItemArray = getSelectedItems("typeOfPlace");
    const List =
      typeOfPlaceList &&
      typeOfPlaceList.map((pt) => {
        const typeId = _.get(pt, "PropertyTypeId", "");
        const typeText = _.get(pt, "PropertyTypeText", "");
        const isChecked =
          selectedItemArray && _.includes(selectedItemArray, typeId);
        return (
          <label
            key={typeId}
            className="d-block mb-2 form-check-label checkbox-label"
            htmlFor={typeId}
          >
            <input
              className="form-check-input"
              name="chkPlace"
              type="checkbox"
              value={typeId ? typeId : ""}
              id={typeId}
              checked={isChecked}
              onChange={(e) => handleTypeOfPlaceClick(e)}
            />
            {typeText}
          </label>
        );
      });

    return List;
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      updateFilter({ search: searchKeyword });
    }, 500);

    return () => clearTimeout(timer);
  }, [searchKeyword]);

  const handleKeyWordSearch = (e) => {
    setSearchKeyword(e.target.value);
  };

  // [US-003] Handle dynamic cap from PriceHistogramSlider
  const handleCapCalculated = (capInfo) => {
    const effectiveMax = dynamicMax || DEFAULT_MAX_BUDGET;
    setDynamicMax(capInfo.cap);
    setDynamicStep(capInfo.step);
    setIsCapFallback(capInfo.fallback);
    // If slider was at old max, auto-update to new max (skip API call via ref flag)
    if (budgetRange && budgetRange[1] === effectiveMax) {
      capAdjustingRef.current = true;
      setBudgetRange([budgetRange[0], capInfo.cap]);
    }
  };

  const handleBudgetMinChange = (e) => {
    const budgetMax = budgetRange && budgetRange[1];
    const value = handleNegativeInput(e.target.value);
    if (value) {
      setBudgetRange([parseInt(replaceComma(value)), budgetMax]);
    } else {
      setBudgetRange([null, budgetMax]);
    }
  };

  const handleBudgetMaxChange = (e) => {
    const budgetMin = budgetRange && budgetRange[0];
    const value = handleNegativeInput(e.target.value);
    const effectiveMax = dynamicMax || DEFAULT_MAX_BUDGET;
    if (parseInt(replaceComma(value))) {
      // [US-003] Clamp to dynamic max
      const parsed = Math.min(parseInt(replaceComma(value)), effectiveMax);
      setBudgetRange([budgetMin, parsed]);
    } else {
      setBudgetRange([budgetMin, null]);
    }
  };

  const handleSquareFootMinChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMinValue(e);
    updateFilter({ sqrftMin: value });
  };

  const handleSquareFootMaxChange = (e) => {
    const value = _.get(e, "value", "");
    setSquareFootageMaxValue(e);
    updateFilter({ sqrftMax: value });
  };

  /**
   * To handle number of bedrooms selection
   * @returns
   */
  const handleBedRoomClick = (e) => {
    const noOfBedrooms = getSelectedItems("bedrooms");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBedrooms) {
      updateFilter({ bedrooms: null });
    } else {
      updateFilter({ bedrooms: selectedValue });
    }
  };
  /**
   * To handle number of beds selection
   * @returns
   */
  const handleBedsClick = (e) => {
    const noOfBeds = getSelectedItems("beds");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBeds) {
      updateFilter({ beds: null });
    } else {
      updateFilter({ beds: selectedValue });
    }
  };
  /**
   * To handle number of bathrooms selection
   * @returns
   */
  const handleBathRoomClick = (e) => {
    const noOfBathrooms = getSelectedItems("baths");
    const value = handleRadioButonChange(e);
    const selectedValue = replaceString(value, "+");
    if (selectedValue == noOfBathrooms) {
      updateFilter({ baths: null });
    } else {
      updateFilter({ baths: selectedValue });
    }
  };

  const addNewOptionsToMinList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMinList(customList);
  };

  const addNewOptionsToMaxList = (list, newOption) => {
    const customList = buildCustomList(list, newOption);
    setSqareFeetMaxList(customList);
  };

  const buildCustomList = (list, newOption) => {
    const parseNumeric = (v) =>
      v === null || v === undefined
        ? null
        : Number(String(v).replace(/[^\d.-]/g, ""));

    const formattedList = list.map((item) => {
      if (item.value === null) return item;
      return {
        value: item.value,
        label: formatSqFtLabel(item.value, item.label),
      };
    });

    const value = parseNumeric(_.get(newOption, "value", null));
    if (value === null) return formattedList;

    const exists = formattedList.some(
      (itm) => parseNumeric(itm.value) === value,
    );
    if (!exists) {
      formattedList.push({
        value,
        label: formatSqFtLabel(value),
      });
    }
    return formattedList;
  };

  const handleOnInputChange = (e, dropSection) => {
    if (dropSection === MIN_SF_DROPDOWN) {
      if (minRef.current && minRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMinList(
            arraySplice(squareFeetMinList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMinValue(options);
          updateFilter({ sqrftMin: e });
        }
      }
    } else {
      if (maxRef.current && maxRef.current.contains(document.activeElement)) {
        if (checkIsNumber(e)) {
          const options = { value: convertToNumber(e), label: e };
          addNewOptionsToMaxList(
            arraySplice(squareFeetMaxList, INDEX_TO_BE_SLICE_15),
            options,
          );
          setSquareFootageMaxValue(options);
          updateFilter({ sqrftMax: e });
        }
      }
    }
  };

  /**
   * To handle number of bedrooms selection view
   * @returns
   */
  const loadBedRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("bedrooms");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of beds selection view
   * @returns
   */
  const loadBeds = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBedrooms = getSelectedItems("beds");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBedrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBedsClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };
  /**
   * To handle number of bathrooms selection view
   * @returns
   */
  const loadBathRooms = () => {
    const items = ["1+", "2+", "3+", "4+", "5+"];
    const noOfBathrooms = getSelectedItems("baths");
    const list =
      items &&
      items.map((itm, index) => {
        const itmValue = replaceString(itm, "+");
        return (
          <button
            type="button"
            value={itmValue}
            className={
              noOfBathrooms === itmValue
                ? "btn btn-primary active"
                : "btn btn-primary"
            }
            key={index}
            onClick={(e) => handleBathRoomClick(e)}
          >
            {itm}
          </button>
        );
      });
    return list;
  };

  const updateReloadParamAfterPlanChange = () => {
    loadUserData();
  };

  if (!filterBarReady) {
    return <FilterBarSkeleton />;
  }

  return (
    <>

      <FilterLocationSearch
        location={location}
        setLocation={setLocation}
        updateFilter={updateFilter}
        adType={adType}
        gitchaNotAvailble={gitchaNotAvailble}
        checkGitchaAvailableInLoc={checkGitchaAvailableInLoc}
        userLocation={userLocation}
        setUserLocation={setUserLocation}
        mlsRestrictedCities={mlsRestrictedCities}
      />
      <FilterKeywordSearch
        handleSearch={handleKeyWordSearch}
        keyWord={searchKeyword}
      />

      <Accordion
        defaultActiveKey={gitchaNotAvailble ? null : "2"}
        alwaysOpen
        className="mt-4"
      >
        {adType !== BUY && (
          <Accordion.Item eventKey="0">
            <Accordion.Header>
              Type of place{" "}
              {getSelectedItems("typeOfPlace").length > 0
                ? `(${getSelectedItems("typeOfPlace").length})`
                : ""}
            </Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">{loadTypeOfPlace()}</div>
            </Accordion.Body>
          </Accordion.Item>
        )}

        <Accordion.Item eventKey="1">
          <Accordion.Header>
            Property type{" "}
            {getSelectedItems("propertyType").length > 0
              ? `(${getSelectedItems("propertyType").length})`
              : ""}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="field-wrap mt-0 mb-0">{loadPropertyMain()}</div>
          </Accordion.Body>
        </Accordion.Item>
        <Accordion.Item eventKey="2">
          <Accordion.Header>{getPriceText(adType)}</Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="slider-sec">
              <div className="pb-4 pt-3">
{/* [US-003] Dynamic cap histogram - slider range adapts to data */}
                <PriceHistogramSlider
                  min={DEFAULT_MIN_BUDGET}
                  max={dynamicMax || DEFAULT_MAX_BUDGET}
                  value={
                    budgetRange && [
                      budgetRange[0] == null
                        ? DEFAULT_MIN_BUDGET
                        : budgetRange[0],
                      budgetRange[1] == null
                        ? (dynamicMax || DEFAULT_MAX_BUDGET)
                        : budgetRange[1],
                    ]
                  }
                  stepValue={dynamicStep || DEFAULT_STEP_VALUE}
                  onInput={setBudgetRange}
                  priceData={adList || []}
                  adType={adType}
                  onCapCalculated={handleCapCalculated}
                />
              </div>
{/* [US-005] Pill-shaped price inputs with Minimum/Maximum labels */}
              <ul className="range-values-list rs-pb-37">
                <li className="position-relative">
                  <span className="price-input-label">Minimum</span>
                  <span className="dollar-sign-icon">$</span>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={minError}
                    value={
                      budgetRange && budgetRange[0] === ""
                        ? ""
                        : thousandSeperatedValueFilter(budgetRange[0])
                    }
                    placeholder="0"
                    onChange={(e) => {
                      handleBudgetMinChange(e);
                    }}
                  ></TextBox>
                  <div className="error-msg"></div>
                </li>
                <li className="text-center">
                  <span className="middle-separator">-</span>
                </li>
                <li className="position-relative">
                  <span className="price-input-label">Maximum</span>
                  <span className="dollar-sign-icon">$</span>

                  <TextBox
                    type="text"
                    className="form-control pad-left-25"
                    errorMessage={maxError}
                    value={
                      budgetRange && budgetRange[1] === (dynamicMax || DEFAULT_MAX_BUDGET)
                        ? (isCapFallback ? "No max" : thousandSeperatedValue(budgetRange[1]) + "+")
                        : thousandSeperatedValue(budgetRange[1])
                    }
                    placeholder="No max"
                    onChange={(e) => {
                      handleBudgetMaxChange(e);
                    }}
                  ></TextBox>
                </li>
              </ul>
            </div>
          </Accordion.Body>
        </Accordion.Item>
        {adType === "STR" && (
          <Accordion.Item eventKey="7">
            <Accordion.Header>Dates</Accordion.Header>
            <Accordion.Body className="pt-0">
              <DateRangeSelector
                startDate={startDate}
                setStartDate={setStartDate}
                endDate={endDate}
                setEndDate={setEndDate}
              />
            </Accordion.Body>
          </Accordion.Item>
        )}
        <Accordion.Item eventKey="3">
          <Accordion.Header>
            {adType === "STR" ? "Rooms and beds" : "Beds and baths"}
          </Accordion.Header>
          <Accordion.Body className="pt-0">
            <div className="lp-btn-group-wrap">
              <label className="form-label">
                {adType === "STR" ? "Bedrooms" : "Beds"}
              </label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBedRooms()}
              </div>
            </div>

            {adType === "STR" && isPrivateRoomSelected && (
              <div className="lp-btn-group-wrap">
                <label className="form-label">Beds</label>
                <div
                  className="btn-group"
                  role="group"
                  aria-label="Basic example"
                >
                  {loadBeds()}
                </div>
              </div>
            )}

            <div className="lp-btn-group-wrap mb-3">
              <label className="form-label">Baths</label>
              <div
                className="btn-group"
                role="group"
                aria-label="Basic example"
              >
                {loadBathRooms()}
              </div>
            </div>
          </Accordion.Body>
        </Accordion.Item>

        {adType !== "STR" && (
          <Accordion.Item eventKey="4">
            <Accordion.Header>Square footage</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="slider-sec">
                <ul className="range-values-list list-pad-fix rs-pb-37">
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={minRef}>
                      <SelectDropDown
                        id={MIN_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMinList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        value={squareFootageMinValue}
                        onChange={(e) => handleSquareFootMinChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MIN_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                  <li className="text-center">
                    <span className="middle-separator">-</span>
                  </li>
                  <li className="position-relative">
                    <label className="d-none"></label>
                    <div ref={maxRef}>
                      <SelectDropDown
                        id={MAX_SF_DROPDOWN}
                        name="colors33"
                        options={squareFeetMaxList}
                        className="lotsize-select"
                        classNamePrefix="select"
                        placeholder="Select"
                        value={squareFootageMaxValue}
                        onChange={(e) => handleSquareFootMaxChange(e)}
                        onInputChange={(e) =>
                          handleOnInputChange(e, MAX_SF_DROPDOWN)
                        }
                        menuPlacement="top"
                        styles={{
                          dropdownIndicator: (provided, state) => ({
                            ...provided,
                            transform:
                              state.selectProps.menuIsOpen && "rotate(180deg)",
                          }),
                        }}
                        errorClass="error-msg error-msg-height"
                      />
                    </div>
                  </li>
                </ul>
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
        {adType === "STR" && (
          <Accordion.Item eventKey="6">
            <Accordion.Header>Additional requirements</Accordion.Header>
            <Accordion.Body className="pt-0">
              <div className="field-wrap mt-0 mb-0">
                {loadAdditionalRequirements()}
              </div>
            </Accordion.Body>
          </Accordion.Item>
        )}
      </Accordion>

      <div className="mt-2">
        <a role="button" className="reset-btn" onClick={() => clearFilter()}>
          Clear all
        </a>
      </div>

      <PremiumPlanModal
        userDetails={userDetailsFromHook}
        show={showPremiumPlanModal}
        onHide={PremiumPlanModalClose}
        updateReloadParam={updateReloadParamAfterPlanChange}
        fromInventoryListing={false}
        showPlanModal={PremiumPlanModalShow}
      />
    </>
  );
};
export default LeftpanelTabContent;
